import React from 'react';
import { cn } from '../../lib/cn';
import { ActionButton, GhostButton } from './buttons';

export type StatusTone = 'neutral' | 'success' | 'warning' | 'error' | 'info';

const toneDot: Record<StatusTone, string> = {
  neutral: 'bg-dg-fg-muted',
  success: 'bg-dg-semantic-success',
  warning: 'bg-dg-semantic-warning',
  error: 'bg-dg-semantic-error',
  info: 'bg-dg-semantic-info',
};

export function StatusBadge({
  children,
  tone = 'neutral',
  className,
}: {
  children: React.ReactNode;
  tone?: StatusTone;
  className?: string;
}): React.ReactElement {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-dg-border-subtle bg-dg-bg-muted px-2.5 py-1 text-dg-label font-medium text-dg-fg-secondary dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300',
        className
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', toneDot[tone])} aria-hidden />
      {children}
    </span>
  );
}

export function ChipTag({
  children,
  selected,
  onClick,
  className,
}: {
  children: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}): React.ReactElement {
  const Comp = onClick ? 'button' : 'span';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-full border px-3 py-1 text-dg-label font-medium transition-colors',
        selected
          ? 'border-dg-primary-500 bg-dg-primary-50 text-dg-primary-700 dark:border-dg-primary-600 dark:bg-dg-primary-950/50 dark:text-dg-primary-200'
          : 'border-dg-border bg-dg-bg-surface text-dg-fg-secondary hover:bg-dg-interaction-hover dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300',
        onClick && 'min-h-9',
        className
      )}
    >
      {children}
    </Comp>
  );
}

export function LoadingSkeleton({
  className,
  lines = 1,
}: {
  className?: string;
  lines?: number;
}): React.ReactElement {
  return (
    <div className={cn('flex w-full flex-col gap-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 w-full animate-pulse rounded-md bg-dg-border-subtle dark:bg-slate-700"
          style={{ width: i === lines - 1 ? '66%' : '100%' }}
        />
      ))}
    </div>
  );
}

export function InfoPanel({
  title,
  children,
  className,
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'rounded-dg-card border border-dg-border-subtle bg-dg-primary-50/60 p-4 dark:border-dg-primary-900/40 dark:bg-dg-primary-950/30',
        className
      )}
    >
      {title ? (
        <p className="mb-2 text-dg-label font-semibold text-dg-primary-700 dark:text-dg-primary-200">
          {title}
        </p>
      ) : null}
      <div className="text-dg-secondary text-dg-fg-secondary dark:text-slate-300">{children}</div>
    </div>
  );
}

export function TabSwitcher<T extends string>({
  tabs,
  value,
  onChange,
  className,
}: {
  tabs: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
}): React.ReactElement {
  return (
    <div
      role="tablist"
      className={cn(
        'flex gap-1 rounded-dg-control border border-dg-border bg-dg-bg-muted p-1 dark:border-slate-700 dark:bg-slate-900',
        className
      )}
    >
      {tabs.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            className={cn(
              'min-h-9 flex-1 rounded-dg-control px-3 text-dg-label font-medium transition-colors',
              active
                ? 'bg-dg-bg-surface text-dg-fg-primary shadow-sm dark:bg-slate-800 dark:text-white'
                : 'text-dg-fg-muted hover:text-dg-fg-primary dark:text-slate-400 dark:hover:text-slate-200'
            )}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export function SegmentedControl<T extends string>(props: {
  segments: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
}): React.ReactElement {
  return (
    <TabSwitcher
      tabs={props.segments}
      value={props.value}
      onChange={props.onChange}
      className={props.className}
    />
  );
}

export function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
  active,
  className,
}: {
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  className?: string;
}): React.ReactElement {
  const cls = cn(
    'flex w-full min-w-0 items-center gap-3 rounded-dg-control border px-3 py-2.5 text-left transition-colors',
    active
      ? 'border-dg-primary-200 bg-dg-primary-50 dark:border-dg-primary-800 dark:bg-dg-primary-950/40'
      : 'border-dg-border bg-dg-bg-surface hover:bg-dg-interaction-hover dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800',
    className
  );
  const inner = (
    <>
      {leading}
      <div className="min-w-0 flex-1">
        <div className="truncate text-dg-body font-medium text-dg-fg-primary dark:text-slate-100">
          {title}
        </div>
        {subtitle ? (
          <div className="truncate text-dg-secondary text-dg-fg-muted dark:text-slate-400">
            {subtitle}
          </div>
        ) : null}
      </div>
      {trailing}
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        {inner}
      </button>
    );
  }
  return <div className={cls}>{inner}</div>;
}

export function ProgressBar({
  value,
  max = 100,
  className,
}: {
  value: number;
  max?: number;
  className?: string;
}): React.ReactElement {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div
      className={cn(
        'h-2 w-full overflow-hidden rounded-full bg-dg-bg-muted dark:bg-slate-800',
        className
      )}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-dg-primary-500 transition-[width] duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function UserAvatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}): React.ReactElement {
  const sz =
    size === 'sm'
      ? 'h-8 w-8 text-dg-label'
      : size === 'lg'
        ? 'h-12 w-12 text-dg-body'
        : 'h-10 w-10 text-dg-secondary';
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-dg-primary-100 font-semibold text-dg-primary-700 dark:bg-dg-primary-950 dark:text-dg-primary-200',
        sz,
        className
      )}
      aria-hidden={!!src}
    >
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );
}

const submissionTone: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  submitted: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  graded: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  returned: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200',
};

export function SubmissionStatusPill({
  status,
  className,
}: {
  status: string;
  className?: string;
}): React.ReactElement {
  const key = status.toLowerCase();
  const cls =
    submissionTone[key] ??
    'bg-dg-bg-muted text-dg-fg-secondary dark:bg-slate-800 dark:text-slate-300';
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-dg-label font-medium', cls, className)}>
      {status}
    </span>
  );
}

export function RubricRow({
  criterion,
  points,
  selected,
  onSelect,
  className,
}: {
  criterion: string;
  points: string;
  selected?: boolean;
  onSelect?: () => void;
  className?: string;
}): React.ReactElement {
  const cls = cn(
    'flex w-full items-start justify-between gap-3 rounded-dg-control border px-3 py-2.5 text-left',
    selected
      ? 'border-dg-primary-400 bg-dg-primary-50 dark:border-dg-primary-600 dark:bg-dg-primary-950/50'
      : 'border-dg-border bg-dg-bg-surface dark:border-slate-700 dark:bg-slate-900',
    onSelect && 'cursor-pointer hover:bg-dg-interaction-hover dark:hover:bg-slate-800',
    className
  );
  if (onSelect) {
    return (
      <button type="button" onClick={onSelect} className={cls}>
        <span className="text-dg-body text-dg-fg-primary dark:text-slate-100">{criterion}</span>
        <span className="shrink-0 text-dg-label font-semibold text-dg-fg-muted dark:text-slate-400">
          {points}
        </span>
      </button>
    );
  }
  return (
    <div className={cls}>
      <span className="text-dg-body text-dg-fg-primary dark:text-slate-100">{criterion}</span>
      <span className="shrink-0 text-dg-label font-semibold text-dg-fg-muted dark:text-slate-400">
        {points}
      </span>
    </div>
  );
}

export function CommentBankItem({
  text,
  onUse,
  className,
}: {
  text: string;
  onUse: () => void;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-dg-control border border-dg-border bg-dg-bg-surface p-3 dark:border-slate-700 dark:bg-slate-900',
        className
      )}
    >
      <p className="text-dg-secondary text-dg-fg-secondary dark:text-slate-300">{text}</p>
      <GhostButton type="button" className="self-start min-h-0 py-1" onClick={onUse}>
        Use
      </GhostButton>
    </div>
  );
}

export function FilterBar({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-dg-card border border-dg-border bg-dg-bg-muted p-2 dark:border-slate-700 dark:bg-slate-900/80',
        className
      )}
    >
      {children}
    </div>
  );
}

export function ClassSelector({
  label,
  value,
  onChange,
  options,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { id: string; name: string }[];
  className?: string;
}): React.ReactElement {
  return (
    <label className={cn('flex min-w-0 flex-col gap-1', className)}>
      <span className="text-dg-label font-medium text-dg-fg-muted dark:text-slate-400">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-11 w-full min-w-0 rounded-dg-control border border-dg-border bg-dg-bg-surface px-3 text-dg-body dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export function DatePill({
  children,
  active,
  onClick,
  className,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  className?: string;
}): React.ReactElement {
  const Comp = onClick ? 'button' : 'span';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-9 items-center rounded-full border px-3 py-1 text-dg-label font-medium transition-colors',
        active
          ? 'border-dg-accent-schedule bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-100'
          : 'border-dg-border bg-dg-bg-surface text-dg-fg-secondary hover:bg-dg-interaction-hover dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300',
        className
      )}
    >
      {children}
    </Comp>
  );
}

export function QuickActionTile({
  icon,
  title,
  description,
  onClick,
  disabled,
  accent = 'home',
  className,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  onClick: () => void;
  disabled?: boolean;
  accent?: 'home' | 'plan' | 'teach' | 'grade' | 'schedule' | 'communicate' | 'options';
  className?: string;
}): React.ReactElement {
  const ring = {
    home: 'focus-visible:ring-dg-accent-home/40',
    plan: 'focus-visible:ring-dg-accent-plan/40',
    teach: 'focus-visible:ring-dg-accent-teach/40',
    grade: 'focus-visible:ring-dg-accent-grade/40',
    schedule: 'focus-visible:ring-dg-accent-schedule/40',
    communicate: 'focus-visible:ring-dg-accent-communicate/40',
    options: 'focus-visible:ring-dg-accent-options/40',
  }[accent];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full flex-col items-start gap-2 rounded-dg-card border border-dg-border bg-dg-bg-surface p-4 text-left shadow-dg-card transition-all hover:shadow-dg-card-hover active:scale-[0.99] disabled:pointer-events-none disabled:opacity-45 dark:border-slate-700 dark:bg-slate-900 dark:shadow-dg-soft-dark',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950',
        ring,
        className
      )}
    >
      <div className="text-dg-fg-primary dark:text-slate-100">{icon}</div>
      <div>
        <p className="text-dg-card-title font-semibold text-dg-fg-primary dark:text-slate-50">
          {title}
        </p>
        {description ? (
          <p className="mt-1 text-dg-secondary text-dg-fg-muted dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
    </button>
  );
}

/** Design-system empty block (distinct from `components/EmptyState.tsx`). */
export function SectionEmptyState({
  title,
  description,
  actionLabel,
  onAction,
  icon,
  className,
}: {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-dg-card border border-dashed border-dg-border bg-dg-bg-muted/50 px-6 py-10 text-center dark:border-slate-600 dark:bg-slate-900/40',
        className
      )}
    >
      {icon ? <div className="mb-3 text-dg-fg-muted">{icon}</div> : null}
      <h3 className="text-dg-card-title font-semibold text-dg-fg-primary dark:text-slate-100">
        {title}
      </h3>
      {description ? (
        <p className="mt-2 max-w-sm text-dg-secondary text-dg-fg-muted dark:text-slate-400">
          {description}
        </p>
      ) : null}
      {actionLabel && onAction ? (
        <ActionButton type="button" className="mt-5 w-auto min-w-[8rem]" onClick={onAction}>
          {actionLabel}
        </ActionButton>
      ) : null}
    </div>
  );
}
